// web-vite/src/app/useSupabaseSubscription.ts
//
// One-shot hook that combines an initial SELECT with a postgres_changes
// realtime subscription. Returns `{rows, loading, error}` and keeps `rows`
// in sync with INSERT/UPDATE/DELETE events on the target table.
//
// Designed for V1 dispatcher screens — read-only, single-mine, single-table.
// Multi-table screens (e.g. Activity Feed = tag_events + location_events)
// compose two subscriptions and merge in the component.
//
// Why not Redux? Server-state belongs next to the component that renders
// it; storing it in a global store doubles the cache and complicates
// invalidation. Subscriptions auto-unsubscribe on unmount.

import {useEffect, useRef, useState} from 'react'
import type {
  RealtimeChannel,
  RealtimePostgresChangesPayload,
} from '@supabase/supabase-js'
import {supabase, DEFAULT_MINE_ID} from './supabaseClient'

export interface UseSupabaseSubscriptionOptions {
  /** Supabase table to subscribe to (and SELECT from). */
  table: string
  /** Columns to SELECT. Defaults to '*'. */
  columns?: string
  /** ORDER BY clause. Tuple of [column, ascending]. */
  orderBy?: [string, boolean]
  /**
   * If true (default), filters the SELECT and realtime subscription by
   * `mine_id=eq.${DEFAULT_MINE_ID}`. Set to false for tables without a
   * mine_id column (e.g. tag_events doesn't have one in the unified schema).
   */
  scopedToMine?: boolean
  /**
   * Custom Supabase filter string for the realtime subscription (e.g.
   * `personnel_id=eq.X`). Combined with mine_id when scopedToMine is true.
   * Note: postgres_changes only supports ONE filter — when both are
   * specified, this string wins.
   */
  filter?: string
  /**
   * Events to subscribe to. Defaults to all (`'*'`). Pass a narrower set
   * (e.g. just 'INSERT' for an append-only activity feed) to reduce
   * realtime traffic.
   */
  events?: Array<'INSERT' | 'UPDATE' | 'DELETE'>
}

export interface UseSupabaseSubscriptionResult<T> {
  rows: T[]
  loading: boolean
  error: string | null
  /** Manual refetch — useful after a screen-level mutation. */
  refetch: () => Promise<void>
}

interface HasId {
  id: string | number
}

/**
 * Pulls rows from a Supabase table and keeps them live via postgres_changes.
 * Generic over the row shape; the caller specifies what columns it SELECTs
 * and matches the type accordingly.
 */
export function useSupabaseSubscription<T extends HasId>(
  options: UseSupabaseSubscriptionOptions,
): UseSupabaseSubscriptionResult<T> {
  const {
    table,
    columns = '*',
    orderBy,
    scopedToMine = true,
    filter,
    events = ['INSERT', 'UPDATE', 'DELETE'],
  } = options

  const [rows, setRows] = useState<T[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // The subscribe-on-mount + cleanup-on-unmount pattern is delicate under
  // React 18 StrictMode (effects fire twice in dev). Use a ref to track
  // whether the current effect run is still the "live" one; abandoned runs
  // skip state updates so we don't double-write rows.
  const liveRef = useRef(0)

  // Refetch closure stays stable enough to use directly in JSX without
  // listing it in the dependency array. Recreated only when key options
  // change.
  const refetch = async () => {
    liveRef.current += 1
    const myRun = liveRef.current
    setLoading(true)
    let query = supabase.from(table).select(columns)
    if (scopedToMine) {
      query = (query as any).eq('mine_id', DEFAULT_MINE_ID)
    }
    if (orderBy) {
      query = (query as any).order(orderBy[0], {ascending: orderBy[1]})
    }
    const {data, error: err} = await query
    if (liveRef.current !== myRun) return
    if (err) {
      setError(`${err.message} (code=${err.code ?? 'n/a'})`)
      setLoading(false)
      return
    }
    setRows((data ?? []) as unknown as T[])
    setError(null)
    setLoading(false)
  }

  useEffect(() => {
    let cancelled = false
    let channel: RealtimeChannel | null = null

    async function load() {
      liveRef.current += 1
      const myRun = liveRef.current
      let query = supabase.from(table).select(columns)
      if (scopedToMine) {
        query = (query as any).eq('mine_id', DEFAULT_MINE_ID)
      }
      if (orderBy) {
        query = (query as any).order(orderBy[0], {ascending: orderBy[1]})
      }
      const {data, error: err} = await query
      if (cancelled || liveRef.current !== myRun) return
      if (err) {
        setError(`${err.message} (code=${err.code ?? 'n/a'})`)
        setLoading(false)
        return
      }
      setRows((data ?? []) as unknown as T[])
      setError(null)
      setLoading(false)
    }

    load()

    // Build the realtime filter. If the caller supplied a custom filter, use
    // it as-is. Otherwise scope to mine_id when requested.
    const realtimeFilter =
      filter ?? (scopedToMine ? `mine_id=eq.${DEFAULT_MINE_ID}` : undefined)

    // Subscribe to each event type the caller wants. Supabase supports '*'
    // but we register them individually so a typo in `events` is caught at
    // build time rather than silently swallowing events.
    channel = supabase.channel(`tagboard-${table}-${Math.random()}`)
    for (const event of events) {
      channel = channel.on(
        'postgres_changes' as any,
        {
          event,
          schema: 'public',
          table,
          ...(realtimeFilter ? {filter: realtimeFilter} : {}),
        },
        (payload: RealtimePostgresChangesPayload<T>) => {
          if (cancelled) return
          if (payload.eventType === 'DELETE') {
            const oldId = (payload.old as Partial<T> | undefined)?.id
            if (oldId != null) {
              setRows(prev => prev.filter(r => r.id !== oldId))
            }
            return
          }
          // INSERT or UPDATE: upsert by id.
          const next = payload.new as T
          if (!next || (next as Partial<T>).id == null) return
          setRows(prev => {
            const idx = prev.findIndex(r => r.id === next.id)
            if (idx === -1) return [...prev, next]
            const copy = prev.slice()
            copy[idx] = {...prev[idx], ...next}
            return copy
          })
        },
      )
    }
    channel.subscribe()

    return () => {
      cancelled = true
      if (channel) supabase.removeChannel(channel)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [table, columns, orderBy?.[0], orderBy?.[1], scopedToMine, filter, events.join(',')])

  return {rows, loading, error, refetch}
}
